import { QueryFactory } from '@lmt/shared-types';
import {
  PatientSearch,
  SearchInputPatientData,
} from '@lmt/unicorn-shared/app/components/common/patient/PatientSearch';
import { useScopedTranslation } from '@lmt/unicorn-shared/app/helpers/localization/useScopedTranslation';
import { useModal } from '@lmt/unicorn-shared/app/components/common/ui-kit/modal/useModal';
import React, { FC, useEffect, useState } from 'react';
import { makeSelectedPatientTitle } from './common';

import styles from './MergePair.module.scss';

interface Props {
  defaultFirstPatientId?: number;
  defaultSecondPatientId?: number;
  setFirstPatientId: (patientId: number | undefined) => void;
  setSecondPatientId: (patientId: number | undefined) => void;
}

export const MergePatientsSearch: FC<Props> = (props) => {
  const {
    setFirstPatientId,
    setSecondPatientId,
    defaultFirstPatientId,
    defaultSecondPatientId,
  } = props;
  const i18next = useScopedTranslation('merge_assistant.merge_pair');
  const modals = useModal();
  const [firstPatientSearchData, setFirstPatientSearchData] =
    useState<SearchInputPatientData | null>(null);
  const [secondPatientSearchData, setSecondPatientSearchData] =
    useState<SearchInputPatientData | null>(null);

  async function showDifferentSubtenantsAlert() {
    await modals.showAlert({
      title: i18next.t('different_subtenants_alert_title'),
      text: i18next.t('different_subtenants_alert_text'),
    });
  }

  async function showTheSamePatientAlert() {
    await modals.showAlert({
      title: i18next.t('same_patient_alert_title'),
      text: i18next.t('same_patient_alert_text'),
    });
  }

  const defaultPatientIds: number[] | null =
    defaultFirstPatientId && defaultSecondPatientId
      ? [defaultFirstPatientId, defaultSecondPatientId]
      : null;

  const defaultPatients = QueryFactory.PatientQuery.useGetPatientsQuery(
    {
      patientIds: defaultPatientIds,
    },
    {
      enabled: !!defaultFirstPatientId && !!defaultSecondPatientId,
    },
  );

  useEffect(() => {
    const patients = defaultPatients.data?.data;
    if (patients) {
      const firstPatient = patients.find(
        (patient) => patient.id === defaultFirstPatientId,
      );
      const secondPatient = patients.find(
        (patient) => patient.id === defaultSecondPatientId,
      );
      setFirstPatientSearchData(firstPatient ?? null);
      setSecondPatientSearchData(secondPatient ?? null);
    }
  }, [defaultPatients.data?.data.length]);

  return (
    <>
      <div></div>
      <div>
        <PatientSearch
          selectedPatient={firstPatientSearchData}
          className={styles.patientSearch}
          isOnlyActive={false}
          getOptionLabel={makeSelectedPatientTitle}
          onSelectedPatientChanged={(patient) => {
            if (patient && secondPatientSearchData?.id === patient.id) {
              showTheSamePatientAlert();
            } else if (
              !secondPatientSearchData ||
              !patient ||
              secondPatientSearchData.subtenantId === patient.subtenantId
            ) {
              setFirstPatientSearchData(patient);
              setFirstPatientId(patient?.id);
            } else {
              setFirstPatientSearchData(null);
              setFirstPatientId(undefined);
              showDifferentSubtenantsAlert();
            }
          }}
          testId="first-patient-search-data"
        />
      </div>
      <div className={styles.patientSearchSpaceColumn}></div>
      <div>
        <PatientSearch
          selectedPatient={secondPatientSearchData}
          className={styles.patientSearch}
          isOnlyActive={false}
          getOptionLabel={makeSelectedPatientTitle}
          onSelectedPatientChanged={(patient) => {
            if (patient && firstPatientSearchData?.id === patient.id) {
              showTheSamePatientAlert();
            } else if (
              !firstPatientSearchData ||
              !patient ||
              firstPatientSearchData.subtenantId === patient.subtenantId
            ) {
              setSecondPatientSearchData(patient);
              setSecondPatientId(patient?.id);
            } else {
              setSecondPatientSearchData(null);
              setSecondPatientId(undefined);
              showDifferentSubtenantsAlert();
            }
          }}
          testId="second-patient-search-data"
        />
      </div>
    </>
  );
};
